import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Campaign, Features, Organization, Person, ReduxState } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { isEngagementSurveyOnlyCampaign } from 'utils/models/Performance';
import { peopleIdsAreEqual } from 'utils/models/Person';
import AttachedContentTypeCommentsProvider from 'views/Widgets/Comments/AttachedContentTypeCommentsProvider';
import ObjectsDropdown from 'views/Widgets/Dropdowns/ObjectsDropdown';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import * as consts from '../../consts/consts';
import { CAMPAIGN_STATUSES } from '../../utils/models/Campaign';
import PersonPerformance from '../Person/PersonPerformance';
import CustomSurveyResponsesViewer, {
  SurveyParticipation,
} from './CustomSurveyResponsesViewer';

const SURVEY_TYPE_PERIODIC = 'PERIODIC';

// a single entry in the unified dropdown: either a performance cycle or a survey
interface DropdownItem {
  // composite key so campaigns and surveys can't collide on numeric id
  key: string;
  kind: 'campaign' | 'survey';
  id: number;
  // used for sorting (campaign coverage end date / survey creation date)
  date: string;
  name: string;
}

interface Props {
  person: Person;
  actions: JSX.Element;
  isAdminable: boolean;
  showManagerOnlyPerformanceDetails: boolean;
  meId?: number;
  features?: Features;
  currentOrganization?: Organization;
}

const SurveysAndPerformancePage: FC<Props> = ({
  person,
  actions,
  isAdminable,
  showManagerOnlyPerformanceDetails,
  meId,
  features,
  currentOrganization,
}) => {
  const { formatMessage, formatDate } = useIntl();
  const history = useHistory();
  const location = useLocation();

  const isMe: boolean = peopleIdsAreEqual(person?.id, meId);
  // surveys are self-only, so they only appear when viewing your own profile
  const surveysEnabled = isMe && !!features?.custom_surveys?.enabled;

  // fetch the performance cycles the person participates in
  const campaignsParams = useMemo(
    () => ({
      status: CAMPAIGN_STATUSES.ACTIVE,
      organization: currentOrganization?.id,
      include_participation_for_person: person?.id,
    }),
    [currentOrganization?.id, person?.id]
  );

  const campaignsPostProcess = useMemo(
    () => (data) =>
      ((data?.results ?? []) as Campaign[]).filter(
        (c) => !isEngagementSurveyOnlyCampaign(c)
      ),
    []
  );

  const { data: campaigns, status: campaignsStatus } = useConfirmApi<
    Campaign[]
  >({
    method: 'GET',
    url: '/campaigns',
    params: campaignsParams,
    postProcess: campaignsPostProcess,
    disabled: !person,
  });

  // fetch the person's survey participations (only relevant on your own profile)
  const participationsParams = useMemo(
    () => ({ organization: currentOrganization?.id }),
    [currentOrganization?.id]
  );

  const participationsPostProcess = useMemo(
    () => (data) => (data?.results ?? data ?? []) as SurveyParticipation[],
    []
  );

  const { data: participationsData, status: participationsStatus } =
    useConfirmApi<SurveyParticipation[]>({
      method: 'GET',
      url: '/custom-survey/participations',
      params: participationsParams,
      postProcess: participationsPostProcess,
      disabled: !surveysEnabled,
    });

  const participations = useMemo(
    () => (surveysEnabled ? (participationsData ?? []) : []),
    [surveysEnabled, participationsData]
  );

  const selectedKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const campaignId = params.get('campaign_id');
    const surveyId = params.get('survey_id');

    if (campaignId) return `campaign:${campaignId}`;
    if (surveyId) return `survey:${surveyId}`;
    return undefined;
  }, [location.search]);

  // merged, date-sorted list (newest first) of performance cycles and surveys
  const items = useMemo<DropdownItem[]>(() => {
    const campaignItems: DropdownItem[] = (campaigns ?? []).map((c) => ({
      key: `campaign:${c.id}`,
      kind: 'campaign',
      id: c.id,
      date: c.coverage_end_date,
      name: formatMessage(
        {
          id: 'app.views.custom_surveys.surveys_and_performance_page.campaign_name_cycle',
          defaultMessage: '{campaignName} cycle',
        },
        { campaignName: c.name }
      ),
    }));

    const surveyItems: DropdownItem[] = (participations ?? []).map((p) => ({
      key: `survey:${p.id}`,
      kind: 'survey',
      id: p.id,
      date: p.created_at,
      name:
        p.survey_type === SURVEY_TYPE_PERIODIC
          ? `${p.survey_name} (${formatDate(p.created_at)})`
          : p.survey_name,
    }));

    return [...campaignItems, ...surveyItems].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [campaigns, participations, formatMessage, formatDate]);

  const onSelect = useCallback(
    (e) => {
      // e.target.value is the composite key "campaign:ID" / "survey:ID"
      const [kind, id] = String(e.target.value).split(':');
      const params = new URLSearchParams(location.search);
      // keep a single selection param
      params.delete('campaign_id');
      params.delete('survey_id');
      params.set(kind === 'survey' ? 'survey_id' : 'campaign_id', id);
      history.push({ search: params.toString() });
    },
    [history, location.search]
  );

  // default to the most recent performance cycle (falling back to the most recent
  // item overall when there are no cycles) — items is already sorted newest-first
  const defaultItem = useMemo(
    () => items.find((i) => i.kind === 'campaign') ?? items[0],
    [items]
  );

  // the URL selection wins when it points at a real item, otherwise fall back to
  // the default (e.g. a shared link to a cycle that is no longer in the list)
  const selectedItem = useMemo(
    () => items.find((i) => i.key === selectedKey) ?? defaultItem,
    [items, selectedKey, defaultItem]
  );

  const selectedParticipation = useMemo(
    () =>
      selectedItem?.kind === 'survey'
        ? participations?.find((p) => p.id === selectedItem.id)
        : undefined,
    [selectedItem, participations]
  );

  const isLoading =
    campaignsStatus === 'LOADING' ||
    (surveysEnabled && participationsStatus === 'LOADING');

  if (isLoading) {
    return <Loading />;
  }

  // no performance cycles or surveys to show for this person
  if (items.length === 0) {
    return (
      <EmptyState
        wrapInCard
        title={formatMessage({
          id: 'app.views.custom_surveys.surveys_and_performance_page.empty_state.title',
          defaultMessage: 'Nothing to show yet',
        })}
        subtitle={
          isMe
            ? formatMessage({
                id: 'app.views.custom_surveys.surveys_and_performance_page.empty_state.subtitle_me',
                defaultMessage:
                  'You have no surveys or performance reviews to display yet.',
              })
            : formatMessage(
                {
                  id: 'app.views.custom_surveys.surveys_and_performance_page.empty_state.subtitle_other',
                  defaultMessage:
                    '{name} has no performance reviews to display yet.',
                },
                { name: person?.given_name }
              )
        }
      />
    );
  }

  return (
    <>
      {items.length > 0 && (
        <Row className="justify-content-between align-items-center mb-4">
          <Col className="col-auto">
            <ObjectsDropdown
              objects={items.map((i) => ({ id: i.key, name: i.name }))}
              value={selectedItem?.key}
              onChange={onSelect}
            />
          </Col>
          <Col className="col-auto">
            {selectedItem?.kind === 'survey'
              ? selectedParticipation &&
                !selectedParticipation.is_expired && (
                  <Button
                    color="primary"
                    onClick={() =>
                      history.push(
                        consts
                          .CUSTOM_SURVEY_RESPONSE(formatMessage)
                          .path.replace(
                            ':participationId',
                            String(selectedParticipation.id)
                          )
                      )
                    }
                  >
                    {selectedParticipation.has_answers ? (
                      <FormattedMessage
                        id="app.views.custom_surveys.surveys_and_performance_page.edit_responses"
                        defaultMessage="Edit your responses"
                      />
                    ) : (
                      <FormattedMessage
                        id="app.views.custom_surveys.surveys_and_performance_page.complete_survey"
                        defaultMessage="Complete the survey"
                      />
                    )}
                  </Button>
                )
              : actions}
          </Col>
        </Row>
      )}
      {selectedItem?.kind === 'survey' ? (
        selectedParticipation && (
          <AttachedContentTypeCommentsProvider
            key={selectedParticipation.id}
            contentTypeKey="custom_survey_answer"
            objectIds={[]}
          >
            <CustomSurveyResponsesViewer
              participation={selectedParticipation}
              respondent={person}
              showComments={false}
            />
          </AttachedContentTypeCommentsProvider>
        )
      ) : (
        <PersonPerformance
          isInReviewFlow={false}
          person={person}
          isAdminable={isAdminable}
          showManagerOnlyPerformanceDetails={showManagerOnlyPerformanceDetails}
          selectedCampaignId={
            selectedItem?.kind === 'campaign' ? selectedItem.id : undefined
          }
          showHistory={true}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  meId: state.me?.id,
  features: state.features,
  currentOrganization: state.currentOrganization,
});

export default connect(mapStateToProps)(SurveysAndPerformancePage);
